import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';

/**
 * Pflicht-Modal: jeder Spieler gibt beim nächsten Login seine richtige
 * E-Mail an. Speichert in users/{uid}.contact_email und löscht das Flag.
 */
export default function EmailCollectionModal({ user, userData, onComplete }) {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    !email.trim().toLowerCase().endsWith('@rhein-stars.app');

  const handleSave = async () => {
    if (!isValid) {
      setError('Bitte gib eine gültige, echte E-Mail-Adresse ein.');
      return;
    }
    setSaving(true); setError('');
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        contact_email: email.trim(),
        needsEmailUpdate: false,
        contact_email_updated_at: serverTimestamp(),
      });
      onComplete();
    } catch (err) {
      console.error('Error saving email:', err);
      setError('Fehler beim Speichern. Bitte erneut versuchen.');
    }
    setSaving(false);
  };

  const name = userData?.display_name || userData?.name || user?.displayName || 'Spieler';

  return (
    <div className="fixed inset-0 bg-black/90 z-[110] flex items-center justify-center p-4" data-testid="email-modal">
      <div className="bg-[#12090c] border border-rose-500/30 rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Hallo {name}!</h2>
            <p className="text-sm text-rose-200/60">Bitte gib deine richtige E-Mail an</p>
          </div>
        </div>

        <div className="bg-blue-600/10 border border-blue-500/30 rounded-lg p-3 mb-5">
          <p className="text-sm text-blue-200">
            ℹ️ Damit wir dich erreichen können (Infos, Coins, Ergebnisse). Deine Anmeldung bleibt unverändert.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-rose-200/70 mb-2">
          <Mail className="w-4 h-4" /> Deine E-Mail-Adresse
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="deine@email.de"
          className="w-full bg-[#0d0409] border border-white/20 rounded-lg px-4 py-3 text-white placeholder-rose-200/30 focus:outline-none focus:border-rose-500/50"
          data-testid="email-input"
          autoFocus
        />

        {error && (
          <div className="mt-4 p-3 bg-red-600/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}

        <Button
          onClick={handleSave}
          disabled={saving || !isValid}
          className="w-full mt-6 py-3 bg-gradient-to-r from-rose-600 to-amber-600 hover:from-rose-700 hover:to-amber-700 disabled:opacity-50"
          data-testid="email-save-btn"
        >
          {saving ? 'Speichern...' : 'Speichern & weiter'}
        </Button>
        <p className="text-center text-xs text-rose-200/40 mt-3">
          Ohne E-Mail geht's leider nicht weiter.
        </p>
      </div>
    </div>
  );
}
